import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Spin, Button, theme } from 'antd';
import { SearchOutlined, CloseOutlined, LeftOutlined, RightOutlined, DownOutlined } from '@ant-design/icons';
import { IconCircleDot } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { ITaxonomySector, ITaxonomyFamily, ITaxonomyType } from '../interfaces/heavyDutyMachinery/ITaxonomy';
import { getTaxonomy, findTaxonomyTypeByCode } from '../services/TaxonomyService';
import { TAXONOMY_SECTOR_ICONS, TAXONOMY_FAMILY_ICONS } from '../constants/heavyDutyMachineryOptionIcons';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';

interface TaxonomyPickerProps {
  // Optional here even though a Form.Item usage always supplies both at
  // clone time -- TypeScript only sees the JSX as written, where neither is
  // passed explicitly, same convention as WizardTileGrid's own props.
  value?: string | string[];
  onChange?: (value: string | string[]) => void;
  /** Checkbox-like (many can be on) instead of radio-like (exactly one, closes the picker immediately). */
  multiple?: boolean;
  disabled?: boolean;
  /** Shown on the closed trigger when nothing is selected yet, and as the search input's placeholder. */
  placeholder?: string;
  /** Modal header title -- defaults to a generic "Select a type". */
  title?: string;
  /** Optional per-MachineryType.Code facet count (see IHeavyDutyMachineryFacets.category) shown next to each leaf row -- omitted where the caller's endpoint builds no facets (Operator/Accessory search). */
  facetCounts?: Record<string, number>;
}

type Level = 'sectors' | 'families' | 'types';

/**
 * Sector -> MachineryFamily -> MachineryType drill-down picker (see
 * ITaxonomy.ts) -- the single component every "type of machinery/accessory/
 * operator" field and filter uses, so a guest/host is never shown all 83
 * leaf types flat at once. A closed trigger button opens a Modal with three
 * navigable levels (tap a Sector to see its Families, tap a Family to see
 * its Types); typing in the always-visible search box instead flattens and
 * searches every leaf Type across the whole taxonomy regardless of the
 * current level, grouped by family, same "search escapes the drill-down"
 * pattern HomeSearchCategoryScreen already uses for its own flat list.
 * Deliberately separate from WizardTileGrid, which stays a plain flat tile
 * grid for its own (non-hierarchical) option lists.
 *
 * Selects/stores MachineryType.Code (language-neutral, what Machinery.
 * Category/etc. actually hold) while displaying each node's already-
 * localized Name -- see ITaxonomy.ts's own doc comment on the Code/name
 * split. Re-fetches the taxonomy if the app language changes mid-session.
 */
const TaxonomyPicker: React.FC<TaxonomyPickerProps> = ({ value, onChange, multiple, disabled, placeholder, title, facetCounts }) => {
  const { t, i18n } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const [sectors, setSectors] = useState<ITaxonomySector[] | null>(null);
  const [level, setLevel] = useState<Level>('sectors');
  const [activeSector, setActiveSector] = useState<ITaxonomySector | null>(null);
  const [activeFamily, setActiveFamily] = useState<ITaxonomyFamily | null>(null);
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    getTaxonomy(i18n.language)
      .then((data) => {
        if (!cancelled) setSectors(data);
      })
      .catch(() => {
        if (!cancelled) setSectors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [i18n.language]);

  // Drops any code that no longer resolves against the current taxonomy --
  // stale values left over from before the Sector/Family/MachineryType
  // rewrite (see that migration's own doc entry), still sitting in the DB
  // on records created before it, would otherwise echo back as their own
  // raw code (see triggerLabel's `type ? type.name : code` fallback below)
  // and get silently re-submitted on the next save, which the backend's
  // MachineryCategoryValidator now rejects outright. Guarded on
  // `sectors.length > 0` -- an empty array means the fetch itself failed,
  // not that every current selection is invalid, so this must never run
  // against that or it would wipe out a perfectly valid selection.
  useEffect(() => {
    if (!sectors || sectors.length === 0) return;
    const codes = multiple ? ((value as string[]) ?? []) : value ? [value as string] : [];
    if (codes.length === 0) return;
    const validCodes = codes.filter((code) => findTaxonomyTypeByCode(sectors, code));
    if (validCodes.length === codes.length) return;
    onChange?.(multiple ? validCodes : validCodes[0] ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectors]);

  const selectedCodes = useMemo(() => (multiple ? ((value as string[]) ?? []) : value ? [value as string] : []), [value, multiple]);

  const resetNavigation = () => {
    setLevel('sectors');
    setActiveSector(null);
    setActiveFamily(null);
    setQuery('');
  };

  const openPicker = () => {
    if (disabled) return;
    resetNavigation();
    setOpen(true);
  };

  const closePicker = () => setOpen(false);

  const selectType = (type: ITaxonomyType) => {
    if (multiple) {
      const current = (value as string[]) ?? [];
      onChange?.(current.includes(type.code) ? current.filter((c) => c !== type.code) : [...current, type.code]);
    } else {
      onChange?.(type.code);
      closePicker();
    }
  };

  const triggerLabel = useMemo(() => {
    if (!sectors || selectedCodes.length === 0) return null;
    const names = selectedCodes.map((code) => {
      const type = findTaxonomyTypeByCode(sectors, code);
      return type ? type.name : code;
    });
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 1).join(', ')} ${translate(t, TRANSLATIONS.TAXONOMY_PICKER_MORE_SUFFIX, { count: names.length - 1 })}`;
  }, [sectors, selectedCodes, t]);

  // Every {sector, family, type} triple, flattened -- only built while
  // searching, since a full 83-row scan on every keystroke over an already
  // tiny list is cheap and this avoids keeping it around otherwise.
  const searchResults = useMemo(() => {
    if (!sectors || !query.trim()) return null;
    const needle = query.trim().toLowerCase();
    const rows: { sector: ITaxonomySector; family: ITaxonomyFamily; type: ITaxonomyType }[] = [];
    sectors.forEach((sector) =>
      sector.families.forEach((family) =>
        family.types.forEach((type) => {
          if (type.name.toLowerCase().includes(needle)) rows.push({ sector, family, type });
        })
      )
    );
    return rows;
  }, [sectors, query]);

  const renderRow = (icon: React.ReactNode, label: string, onClick: () => void, opts?: { selected?: boolean; suffix?: React.ReactNode; showChevron?: boolean }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        width: '100%',
        padding: '12px 6px',
        border: 'none',
        background: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        borderBottom: `1px solid ${token.colorBorderSecondary}`,
      }}
    >
      {icon}
      <span style={{ flex: 1, fontSize: 15, color: token.colorText, fontWeight: opts?.selected ? 600 : 400 }}>{label}</span>
      {opts?.suffix}
      {opts?.showChevron !== false && !opts?.selected && <RightOutlined style={{ fontSize: 12, color: token.colorTextTertiary }} />}
      {opts?.selected && (
        <span
          style={{
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: token.colorPrimary,
            display: 'inline-block',
            flexShrink: 0,
          }}
        />
      )}
    </button>
  );

  const iconCircle = (Icon: typeof IconCircleDot) => (
    <div
      style={{
        width: 38,
        height: 38,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: token.colorFillTertiary,
        flexShrink: 0,
      }}
    >
      <Icon size={19} color={token.colorTextSecondary} />
    </div>
  );

  const renderBackHeader = (label: string, onBack: () => void) => (
    <button
      type="button"
      onClick={onBack}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '4px 6px 12px',
        border: 'none',
        background: 'none',
        cursor: 'pointer',
        color: token.colorText,
        fontWeight: 600,
        fontSize: 15,
      }}
    >
      <LeftOutlined style={{ fontSize: 13 }} />
      {label}
    </button>
  );

  const renderBody = () => {
    if (!sectors) {
      return (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin size="small" />
        </div>
      );
    }

    if (query.trim()) {
      if (!searchResults || searchResults.length === 0) {
        return (
          <div style={{ textAlign: 'center', padding: 24, color: token.colorTextTertiary, fontSize: 14 }}>
            {translate(t, TRANSLATIONS.TAXONOMY_PICKER_NO_RESULTS)}
          </div>
        );
      }
      // Grouped by family so a broad search term ("grúa") doesn't read as one undifferentiated wall of rows.
      const groups: { family: ITaxonomyFamily; rows: { sector: ITaxonomySector; family: ITaxonomyFamily; type: ITaxonomyType }[] }[] = [];
      searchResults.forEach((row) => {
        let group = groups.find((g) => g.family.id === row.family.id);
        if (!group) {
          group = { family: row.family, rows: [] };
          groups.push(group);
        }
        group.rows.push(row);
      });
      return groups.map((group) => (
        <div key={group.family.id} style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: token.colorTextTertiary, textTransform: 'uppercase', padding: '10px 6px 4px' }}>
            {group.family.name}
          </div>
          {group.rows.map(({ type }) => {
            const selected = selectedCodes.includes(type.code);
            const count = facetCounts?.[type.code];
            return renderRow(<span style={{ width: 38, flexShrink: 0 }} />, type.name, () => selectType(type), {
              selected: multiple ? selected : false,
              showChevron: false,
              suffix: count != null ? <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{count}</span> : undefined,
            });
          })}
        </div>
      ));
    }

    if (level === 'sectors') {
      return sectors.map((sector) => {
        const Icon = TAXONOMY_SECTOR_ICONS[sector.code] ?? IconCircleDot;
        return renderRow(iconCircle(Icon), sector.name, () => {
          setActiveSector(sector);
          setLevel('families');
        });
      });
    }

    if (level === 'families' && activeSector) {
      return (
        <>
          {renderBackHeader(activeSector.name, resetNavigation)}
          {activeSector.families.map((family) => {
            const Icon = TAXONOMY_FAMILY_ICONS[family.code] ?? IconCircleDot;
            return renderRow(iconCircle(Icon), family.name, () => {
              setActiveFamily(family);
              setLevel('types');
            });
          })}
        </>
      );
    }

    if (level === 'types' && activeFamily) {
      return (
        <>
          {renderBackHeader(activeFamily.name, () => {
            setActiveFamily(null);
            setLevel('families');
          })}
          {activeFamily.types.map((type) => {
            const selected = selectedCodes.includes(type.code);
            const count = facetCounts?.[type.code];
            return renderRow(<span style={{ width: 38, flexShrink: 0 }} />, type.name, () => selectType(type), {
              selected: multiple ? selected : false,
              showChevron: false,
              suffix: count != null ? <span style={{ fontSize: 12, color: token.colorTextTertiary }}>{count}</span> : undefined,
            });
          })}
        </>
      );
    }

    return null;
  };

  return (
    <>
      <button
        type="button"
        onClick={openPicker}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '10px 14px',
          borderRadius: 10,
          border: `1px solid ${token.colorBorder}`,
          background: disabled ? token.colorBgContainerDisabled : token.colorBgContainer,
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          fontSize: 14,
          color: triggerLabel ? token.colorText : token.colorTextTertiary,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {triggerLabel ?? placeholder ?? translate(t, TRANSLATIONS.TAXONOMY_PICKER_DEFAULT_TITLE)}
        </span>
        <DownOutlined style={{ fontSize: 11, color: token.colorTextTertiary, flexShrink: 0, marginLeft: 8 }} />
      </button>

      <Modal
        open={open}
        onCancel={closePicker}
        footer={
          multiple && selectedCodes.length > 0 ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Button type="text" onClick={() => onChange?.([])}>
                {translate(t, TRANSLATIONS.TAXONOMY_PICKER_CLEAR)}
              </Button>
              <Button type="primary" onClick={closePicker}>
                {translate(t, TRANSLATIONS.TAXONOMY_PICKER_SELECTED_COUNT, { count: selectedCodes.length })}
              </Button>
            </div>
          ) : null
        }
        closeIcon={<CloseOutlined />}
        title={title ?? translate(t, TRANSLATIONS.TAXONOMY_PICKER_DEFAULT_TITLE)}
        width="min(560px, 92vw)"
        styles={{ body: { maxHeight: '60vh', overflowY: 'auto', padding: '4px 4px 0' } }}
      >
        <div style={{ marginBottom: 8 }}>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder ?? translate(t, TRANSLATIONS.TAXONOMY_PICKER_SEARCH_PLACEHOLDER)}
            prefix={<SearchOutlined style={{ color: token.colorTextTertiary }} />}
            allowClear
            autoComplete="off"
          />
        </div>
        {renderBody()}
      </Modal>
    </>
  );
};

export default TaxonomyPicker;
